<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-ribbon me-2"></i>{{ $t('page.art','HIV / ART Register') }}</h4>
        </div>
        <button v-if="canAccess" class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
      </div>

      <div v-if="!canAccess" class="hms-card text-center" style="padding:40px; background:linear-gradient(135deg,#fef3c7,#fde68a); border:1px dashed #d97706;">
        <i class="mdi mdi-shield-alert-outline" style="font-size:48px; color:#b45309;"></i>
        <h4 class="mt-2" style="color:#78350f;">Clinical Access Only</h4>
        <p style="color:#92400e;">The HIV / ART register contains protected, sensitive information about people living with HIV. Access is restricted to clinical roles. This access attempt has been audit-logged.</p>
        <button class="hms-btn hms-btn-neutral" @click="$router.push('/dashboard')">Back to Dashboard</button>
      </div>
      <div v-else>

      <!-- STATS -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ stats.currently_on_art || 0 }}</div><div class="hms-stat-lbl">Currently on ART</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ stats.new_30d || 0 }}</div><div class="hms-stat-lbl">New (30d)</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.pregnant_active || 0 }}</div><div class="hms-stat-lbl">Pregnant on ART</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.tb_on_tx || 0 }}</div><div class="hms-stat-lbl">TB co-infection</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ defaulterStats.lost_to_followup || 0 }}</div><div class="hms-stat-lbl">Lost (&gt;90d)</div></div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='clients' }"    @click="tab='clients'"><i class="mdi mdi-account-group me-1"></i>Active Clients</button>
        <button class="hms-tab" :class="{ active: tab==='detail' }"     @click="tab='detail'" :disabled="!detail.client"><i class="mdi mdi-card-account-details me-1"></i>Client Card</button>
        <button class="hms-tab" :class="{ active: tab==='defaulters' }" @click="tab='defaulters'; loadDefaulters()"><i class="mdi mdi-alert me-1"></i>Defaulters</button>
        <button class="hms-tab" :class="{ active: tab==='vl' }"         @click="tab='vl'; loadVlWatch()"><i class="mdi mdi-test-tube me-1"></i>VL Watchlist</button>
        <button class="hms-tab" :class="{ active: tab==='indicators' }" @click="tab='indicators'; loadIndicators()"><i class="mdi mdi-chart-box me-1"></i>MoH 731</button>
      </div>

      <!-- ============ CLIENTS TAB ============ -->
      <div v-if="tab==='clients'">
        <div class="d-flex justify-content-between mb-2">
          <div class="d-flex gap-2">
            <input v-model="clientSearch" class="hms-input" placeholder="Name / ART # / CCC # / phone" @keyup.enter="loadClients" style="min-width:280px" />
            <button class="hms-btn hms-btn-blue" @click="loadClients">Search</button>
          </div>
          <button class="hms-btn hms-btn-success" @click="openEnrolModal()">
            <i class="mdi mdi-account-plus"></i> Enrol New Client
          </button>
        </div>
        <table class="hms-table">
          <thead>
            <tr>
              <th>ART #</th><th>Name</th><th>Sex / Age</th><th>Regimen</th><th>Line</th>
              <th>Latest VL</th><th>Next appt</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in clients" :key="c.art_id" :class="rowClass(c)">
              <td><strong>{{ c.art_number }}</strong><div class="hms-muted-sm">{{ c.ccc_number || '' }}</div></td>
              <td>{{ c.patient_name }}<div class="hms-muted-sm">{{ c.patient_no }} · {{ c.phone || '' }}</div></td>
              <td>{{ c.sex || '—' }} {{ ageString(c.date_of_birth) }}</td>
              <td>{{ c.regimen_code || '—' }}</td>
              <td><span class="hms-pill hms-pill-info">{{ c.regimen_line || '—' }}</span></td>
              <td>
                <span v-if="c.latest_vl != null" :class="c.latest_vl_suppressed == 1 ? 'st-good' : 'st-bad'">{{ c.latest_vl }} c/mL</span>
                <span v-else class="hms-muted-sm">no VL</span>
                <div v-if="c.latest_vl_date" class="hms-muted-sm">{{ c.latest_vl_date }}</div>
              </td>
              <td :class="c.days_to_appointment != null && c.days_to_appointment < 0 ? 'st-bad' : ''">
                {{ c.next_appointment_date || '—' }}
                <div v-if="c.days_to_appointment != null" class="hms-muted-sm">
                  {{ c.days_to_appointment >= 0 ? 'in ' + c.days_to_appointment + 'd' : Math.abs(c.days_to_appointment) + 'd late' }}
                </div>
              </td>
              <td><span class="hms-pill" :class="statusPill(c.current_status)">{{ c.current_status }}</span></td>
              <td>
                <button class="hms-btn hms-btn-blue hms-btn-sm" @click="openDetail(c)"><i class="mdi mdi-folder-open"></i> Open</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ DETAIL TAB ============ -->
      <div v-if="tab==='detail' && detail.client">
        <div class="hms-card mb-3">
          <div class="hms-card-body">
            <div class="d-flex justify-content-between flex-wrap gap-3">
              <div>
                <div class="hms-name-big">{{ detail.client.patient_name }}</div>
                <div class="hms-muted-sm">{{ detail.client.art_number }} · CCC {{ detail.client.ccc_number || '—' }}</div>
                <div class="hms-muted-sm">DOB {{ detail.client.date_of_birth || '—' }} ({{ ageString(detail.client.date_of_birth) }}) · {{ detail.client.sex || '—' }} · {{ detail.client.phone || '' }}</div>
              </div>
              <div class="text-end">
                <div><strong>{{ detail.client.regimen_code || '—' }}</strong> <span class="hms-pill hms-pill-info">{{ detail.client.regimen_line }}</span></div>
                <div class="hms-muted-sm">Started ART {{ detail.client.date_started_art || '—' }}</div>
                <div class="hms-muted-sm">Next appt: <strong>{{ detail.client.next_appointment_date || '—' }}</strong></div>
              </div>
            </div>
            <div class="d-flex gap-2 mt-3">
              <button class="hms-btn hms-btn-success hms-btn-sm" @click="openVisitModal()"><i class="mdi mdi-clipboard-text"></i> Record Visit</button>
              <button class="hms-btn hms-btn-blue hms-btn-sm"    @click="openVlModal()"><i class="mdi mdi-test-tube"></i> Record Viral Load</button>
              <button class="hms-btn hms-btn-amber hms-btn-sm"   @click="openSwitchModal()"><i class="mdi mdi-swap-horizontal"></i> Switch Regimen</button>
              <button class="hms-btn hms-btn-secondary hms-btn-sm" @click="openUpdateModal()"><i class="mdi mdi-pencil"></i> Update Client</button>
            </div>
          </div>
        </div>

        <div class="hms-grid-2">
          <div class="hms-card">
            <div class="hms-card-head">Viral Load Trend</div>
            <div class="hms-card-body">
              <div v-if="!detail.viralLoads.length" class="hms-muted text-center hms-pad">No viral loads on record.</div>
              <table v-else class="hms-table">
                <thead><tr><th>Collected</th><th>Result</th><th>Indication</th><th>Status</th></tr></thead>
                <tbody>
                  <tr v-for="v in detail.viralLoads" :key="v.vl_id" :class="v.is_suppressed == 1 ? '' : 'row-warn'">
                    <td>{{ v.collection_date }}</td>
                    <td>
                      <strong v-if="v.is_undetectable == 1">Undetectable</strong>
                      <span v-else-if="v.vl_copies != null">{{ v.vl_copies }} c/mL</span>
                      <span v-else>pending</span>
                    </td>
                    <td>{{ formatIndication(v.vl_indication) }}</td>
                    <td>
                      <span v-if="v.is_fully_suppressed == 1" class="hms-pill hms-pill-success">&lt;50</span>
                      <span v-else-if="v.is_suppressed == 1" class="hms-pill hms-pill-info">Suppressed</span>
                      <span v-else-if="v.vl_copies != null" class="hms-pill hms-pill-danger">Unsuppressed</span>
                      <span v-else class="hms-pill hms-pill-muted">Pending</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="hms-card">
            <div class="hms-card-head">Regimen History</div>
            <div class="hms-card-body">
              <div v-if="!detail.regimenChanges.length" class="hms-muted text-center hms-pad">No regimen changes.</div>
              <table v-else class="hms-table">
                <thead><tr><th>Date</th><th>From</th><th>To</th><th>Reason</th></tr></thead>
                <tbody>
                  <tr v-for="r in detail.regimenChanges" :key="r.change_id">
                    <td>{{ r.changed_at }}</td>
                    <td>{{ r.from_code || '—' }}</td>
                    <td><strong>{{ r.to_code }}</strong></td>
                    <td>{{ formatChangeReason(r.reason) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="hms-card mt-3">
          <div class="hms-card-head">Visit History</div>
          <div class="hms-card-body p-0">
            <table class="hms-table">
              <thead>
                <tr>
                  <th>Date</th><th>Type</th><th>Wt</th><th>BP</th><th>WHO</th>
                  <th>Adherence</th><th>Regimen</th><th>Days dispensed</th><th>Next appt</th><th>By</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="v in detail.visits" :key="v.visit_id">
                  <td>{{ v.visit_date }}</td>
                  <td><span class="hms-pill hms-pill-muted">{{ v.visit_type }}</span></td>
                  <td>{{ v.weight_kg || '—' }}</td>
                  <td>{{ v.bp_systolic || '—' }}/{{ v.bp_diastolic || '—' }}</td>
                  <td>{{ v.who_stage || '—' }}</td>
                  <td><span class="hms-pill" :class="adherencePill(v.adherence)">{{ v.adherence || '—' }}</span></td>
                  <td>{{ v.visit_regimen_code || '—' }}</td>
                  <td>{{ v.days_dispensed || '—' }}</td>
                  <td>{{ v.next_appointment || '—' }}</td>
                  <td>{{ v.recorded_by_name || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ============ DEFAULTERS TAB ============ -->
      <div v-if="tab==='defaulters'">
        <div v-if="!defaulters.length" class="hms-empty">
          <i class="mdi mdi-check-circle"></i><h5>No defaulters</h5>
        </div>
        <table v-else class="hms-table">
          <thead>
            <tr>
              <th>ART #</th><th>Name</th><th>Regimen</th><th>Last appt</th><th>Days late</th>
              <th>Phone</th><th>Supporter</th><th>CHV</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in defaulters" :key="d.art_id" :class="d.days_late > 90 ? 'row-action' : (d.days_late >= 30 ? 'row-warn' : '')">
              <td><strong>{{ d.art_number }}</strong></td>
              <td>{{ d.patient_name }}</td>
              <td>{{ d.regimen_code || '—' }}</td>
              <td>{{ d.next_appointment_date }}</td>
              <td><strong :class="d.days_late > 30 ? 'st-bad' : ''">{{ d.days_late }}d</strong></td>
              <td>{{ d.phone || '—' }}</td>
              <td>{{ d.treatment_supporter_name || '—' }} <span class="hms-muted-sm">{{ d.treatment_supporter_phone }}</span></td>
              <td>{{ d.chv_name || '—' }} <span class="hms-muted-sm">{{ d.chv_phone }}</span></td>
              <td><button class="hms-btn-link" @click="openDetailById(d.art_id)" title="Open"><i class="mdi mdi-folder-open"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ VL WATCHLIST ============ -->
      <div v-if="tab==='vl'">
        <div v-if="!vlWatch.length" class="hms-empty">
          <i class="mdi mdi-check-circle"></i><h5>All clients have current, suppressed VL</h5>
        </div>
        <table v-else class="hms-table">
          <thead>
            <tr>
              <th>ART #</th><th>Name</th><th>Regimen</th><th>Started ART</th>
              <th>Latest VL</th><th>Days since VL</th><th>Issue</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="w in vlWatch" :key="w.art_id" :class="(w.latest_vl_copies && !w.is_suppressed) ? 'row-action' : 'row-warn'">
              <td><strong>{{ w.art_number }}</strong></td>
              <td>{{ w.patient_name }}</td>
              <td>{{ w.regimen_code || '—' }} ({{ w.regimen_line }})</td>
              <td>{{ w.date_started_art }}</td>
              <td>
                <span v-if="w.latest_vl_copies != null" :class="w.is_suppressed == 1 ? 'st-good' : 'st-bad'">{{ w.latest_vl_copies }} c/mL</span>
                <span v-else class="hms-muted-sm">never done</span>
                <div v-if="w.latest_vl_date" class="hms-muted-sm">{{ w.latest_vl_date }}</div>
              </td>
              <td>{{ w.days_since_vl }}d</td>
              <td>
                <span v-if="!w.latest_vl_date" class="hms-pill hms-pill-warn">No VL</span>
                <span v-else-if="w.is_suppressed == 0" class="hms-pill hms-pill-danger">Unsuppressed</span>
                <span v-else class="hms-pill hms-pill-warn">VL overdue</span>
              </td>
              <td><button class="hms-btn-link" @click="openDetailById(w.art_id)"><i class="mdi mdi-folder-open"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ INDICATORS / 731 ============ -->
      <div v-if="tab==='indicators'">
        <div class="d-flex gap-2 mb-3">
          <select v-model="indicatorPeriod" class="hms-input" @change="loadIndicators" style="max-width:200px">
            <option value="this_month">This month</option>
            <option value="this_quarter">This quarter</option>
            <option value="this_year">This year</option>
          </select>
        </div>
        <div class="hms-grid-2">
          <div class="hms-card">
            <div class="hms-card-head">MoH 731 — Care &amp; Treatment</div>
            <div class="hms-card-body">
              <div class="hms-ind-row"><span>Currently on ART</span><strong>{{ indicators.currently_on_art || 0 }}</strong></div>
              <div class="hms-ind-row"><span>New on ART (period)</span><strong>{{ indicators.new_on_art || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Pregnant on ART</span><strong>{{ indicators.pregnant_on_art || 0 }}</strong></div>
              <div class="hms-ind-row"><span>HIV/TB co-infection</span><strong>{{ indicators.art_with_tb || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Lost to follow-up</span><strong>{{ indicators.lost_to_followup || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Died (period)</span><strong>{{ indicators.died_period || 0 }}</strong></div>
            </div>
          </div>
          <div class="hms-card">
            <div class="hms-card-head">Viral Load (12 months)</div>
            <div class="hms-card-body">
              <div class="hms-ind-row"><span>VL done</span><strong>{{ indicators.vl_done_12m || 0 }}</strong></div>
              <div class="hms-ind-row"><span>VL suppressed (&lt;1000)</span><strong>{{ indicators.vl_suppressed_12m || 0 }}</strong></div>
              <div class="hms-suppression">
                <div class="hms-supp-label">Suppression rate</div>
                <div class="hms-supp-bar"><div class="hms-supp-fill" :style="{ width: suppressionPct + '%' }"></div></div>
                <div class="hms-supp-num">{{ suppressionPct }}%</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- =============== ENROL MODAL =============== -->
    <div v-if="enrolModal" class="hms-modal-bg" @click.self="enrolModal=false">
      <div class="hms-modal" style="width:680px">
        <div class="hms-modal-head"><h5>Enrol New ART Client</h5><button class="hms-icon-btn" @click="enrolModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div v-if="!enrol.patient_id">
            <label class="hms-label">Patient *</label>
            <div class="d-flex gap-2">
              <input v-model="enrol.patient_search" class="hms-input" placeholder="Search by name / patient no / phone" @keyup.enter="findEnrolPatient" />
              <button class="hms-btn hms-btn-blue" @click="findEnrolPatient">Find</button>
            </div>
          </div>
          <div v-else class="hms-patient-chip">
            <i class="mdi mdi-account"></i> {{ enrol.patient_name }} <a href="javascript:void(0)" class="ms-2" @click="enrol.patient_id=null">change</a>
          </div>
          <div class="row g-2 mt-2">
            <div class="col-4"><label class="hms-label">CCC #</label><input v-model="enrol.ccc_number" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Enrolment date *</label><input v-model="enrol.enrollment_date" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Entry point</label>
              <select v-model="enrol.entry_point" class="hms-input">
                <option value="OPD">OPD</option><option value="VCT">VCT</option><option value="IPD">IPD</option>
                <option value="PMTCT">PMTCT (ANC/MCH)</option><option value="TB">TB clinic</option>
                <option value="paeds">Paediatric</option><option value="exposed_infant">HIV-exposed infant</option>
                <option value="self_test">Self-test</option><option value="sexual_assault">SGBV/PEP</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">WHO stage</label>
              <select v-model.number="enrol.who_stage" class="hms-input">
                <option :value="null">—</option>
                <option :value="1">I</option><option :value="2">II</option>
                <option :value="3">III</option><option :value="4">IV</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Baseline CD4</label><input v-model.number="enrol.baseline_cd4" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">CD4 date</label><input v-model="enrol.baseline_cd4_date" type="date" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Baseline VL</label><input v-model.number="enrol.baseline_vl" type="number" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Date started ART</label><input v-model="enrol.date_started_art" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Regimen *</label>
              <select v-model.number="enrol.current_regimen_id" class="hms-input">
                <option :value="null">— select —</option>
                <option v-for="r in regimens" :key="r.regimen_id" :value="r.regimen_id">{{ r.code }} — {{ r.name }}</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Line</label>
              <select v-model="enrol.regimen_line" class="hms-input">
                <option value="1st">1st line</option><option value="2nd">2nd line</option>
                <option value="3rd">3rd line</option><option value="PrEP">PrEP</option><option value="PEP">PEP</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Pregnancy</label>
              <select v-model="enrol.pregnancy_status" class="hms-input">
                <option value="not_applicable">N/A</option><option value="not_pregnant">Not pregnant</option>
                <option value="pregnant">Pregnant</option><option value="breastfeeding">Breastfeeding</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">TB status</label>
              <select v-model="enrol.tb_status" class="hms-input">
                <option value="not_screened">Not screened</option><option value="no_signs">No signs</option>
                <option value="presumptive">Presumptive</option><option value="on_treatment">On TB tx</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Next appointment</label><input v-model="enrol.next_appointment_date" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Treatment supporter</label><input v-model="enrol.treatment_supporter_name" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Supporter phone</label><input v-model="enrol.treatment_supporter_phone" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">CHV name</label><input v-model="enrol.chv_name" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">CHV phone</label><input v-model="enrol.chv_phone" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="enrol.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="enrolModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !enrol.patient_id" @click="saveEnrol">{{ saving?'Saving…':'Enrol' }}</button>
        </div>
      </div>
    </div>

    <!-- =============== VISIT MODAL =============== -->
    <div v-if="visitModal" class="hms-modal-bg" @click.self="visitModal=false">
      <div class="hms-modal" style="width:680px">
        <div class="hms-modal-head"><h5>Record ART Visit</h5><button class="hms-icon-btn" @click="visitModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-3"><label class="hms-label">Date *</label><input v-model="visit.visit_date" type="date" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Type</label>
              <select v-model="visit.visit_type" class="hms-input">
                <option value="initiation">Initiation</option><option value="refill">Refill</option>
                <option value="review">Clinical review</option><option value="transfer_in">Transfer-in</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Weight (kg)</label><input v-model.number="visit.weight_kg" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Height (cm)</label><input v-model.number="visit.height_cm" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">BP Sys</label><input v-model.number="visit.bp_systolic" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">BP Dia</label><input v-model.number="visit.bp_diastolic" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">WHO</label>
              <select v-model.number="visit.who_stage" class="hms-input">
                <option :value="null">—</option><option :value="1">I</option><option :value="2">II</option><option :value="3">III</option><option :value="4">IV</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Functional</label>
              <select v-model="visit.functional_status" class="hms-input">
                <option value="working">Working</option><option value="ambulatory">Ambulatory</option><option value="bedridden">Bedridden</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Adherence</label>
              <select v-model="visit.adherence" class="hms-input">
                <option value="good">Good</option><option value="fair">Fair</option><option value="poor">Poor</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Pregnancy</label>
              <select v-model="visit.pregnancy_status" class="hms-input">
                <option value="not_applicable">N/A</option><option value="not_pregnant">Not pregnant</option>
                <option value="pregnant">Pregnant</option><option value="breastfeeding">Breastfeeding</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">TB screening</label>
              <select v-model="visit.tb_screening" class="hms-input">
                <option value="no_signs">No signs</option><option value="presumptive">Presumptive</option>
                <option value="on_treatment">On TB tx</option><option value="completed">Completed</option><option value="not_done">Not done</option>
              </select>
            </div>
            <div class="col-3 d-flex align-items-end gap-2">
              <label><input type="checkbox" v-model="visit.cotrim_dispensed" :true-value="1" :false-value="0" /> CTX</label>
              <label><input type="checkbox" v-model="visit.inh_dispensed"    :true-value="1" :false-value="0" /> INH</label>
            </div>
            <div class="col-6"><label class="hms-label">ARVs dispensed</label><input v-model="visit.arvs_dispensed" class="hms-input" placeholder="e.g. TLD x60" /></div>
            <div class="col-3"><label class="hms-label">Days dispensed</label><input v-model.number="visit.days_dispensed" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Next appointment</label><input v-model="visit.next_appointment" type="date" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Side effects / complaints</label><textarea v-model="visit.side_effects" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="visit.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="visitModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveVisit">{{ saving?'Saving…':'Save Visit' }}</button>
        </div>
      </div>
    </div>

    <!-- =============== VL MODAL =============== -->
    <div v-if="vlModal" class="hms-modal-bg" @click.self="vlModal=false">
      <div class="hms-modal" style="width:540px">
        <div class="hms-modal-head"><h5>Record Viral Load</h5><button class="hms-icon-btn" @click="vlModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Collection date *</label><input v-model="vl.collection_date" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Result date</label><input v-model="vl.result_date" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">VL (copies/mL)</label><input v-model.number="vl.vl_copies" type="number" class="hms-input" :disabled="vl.is_undetectable == 1" /></div>
            <div class="col-6 d-flex align-items-end">
              <label><input type="checkbox" v-model="vl.is_undetectable" :true-value="1" :false-value="0" /> Undetectable / LDL</label>
            </div>
            <div class="col-6"><label class="hms-label">Sample</label>
              <select v-model="vl.sample_type" class="hms-input">
                <option value="plasma">Plasma</option><option value="dbs">DBS</option><option value="wholeblood">Whole blood</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Indication</label>
              <select v-model="vl.vl_indication" class="hms-input">
                <option value="routine_6m">Routine 6-month</option>
                <option value="routine_12m">Routine 12-month</option>
                <option value="annual">Annual</option>
                <option value="high_vl_repeat">High VL repeat</option>
                <option value="suspected_failure">Suspected failure</option>
                <option value="clinical_failure">Clinical failure</option>
                <option value="pregnancy">Pregnancy</option>
                <option value="baseline">Baseline</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Lab</label><input v-model="vl.lab_name" class="hms-input" placeholder="e.g. NPHL / KEMRI" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="vl.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="vlModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveVl">{{ saving?'Saving…':'Save VL' }}</button>
        </div>
      </div>
    </div>

    <!-- =============== SWITCH REGIMEN MODAL =============== -->
    <div v-if="switchModal" class="hms-modal-bg" @click.self="switchModal=false">
      <div class="hms-modal" style="width:520px">
        <div class="hms-modal-head"><h5>Switch / Substitute Regimen</h5><button class="hms-icon-btn" @click="switchModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="hms-info-banner">Currently: <strong>{{ detail.client && detail.client.regimen_code }}</strong> ({{ detail.client && detail.client.regimen_line }})</div>
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">New regimen *</label>
              <select v-model.number="switchForm.to_regimen_id" class="hms-input">
                <option :value="null">— select —</option>
                <option v-for="r in regimens" :key="r.regimen_id" :value="r.regimen_id">{{ r.code }} — {{ r.name }} ({{ r.line }})</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Line</label>
              <select v-model="switchForm.to_line" class="hms-input">
                <option value="1st">1st</option><option value="2nd">2nd</option><option value="3rd">3rd</option>
                <option value="PrEP">PrEP</option><option value="PEP">PEP</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Date</label><input v-model="switchForm.changed_at" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Reason</label>
              <select v-model="switchForm.reason" class="hms-input">
                <option value="treatment_failure">Treatment failure</option>
                <option value="side_effects">Side effects / toxicity</option>
                <option value="tb_treatment">TB treatment</option>
                <option value="pregnancy">Pregnancy</option>
                <option value="stockout">Stock-out</option>
                <option value="simplification">Simplification</option>
                <option value="new_guideline">New guideline</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="switchForm.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="switchModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !switchForm.to_regimen_id" @click="saveSwitch">{{ saving?'Saving…':'Switch' }}</button>
        </div>
      </div>
    </div>

    <!-- =============== UPDATE CLIENT MODAL =============== -->
    <div v-if="updateModal" class="hms-modal-bg" @click.self="updateModal=false">
      <div class="hms-modal" style="width:560px">
        <div class="hms-modal-head"><h5>Update Client Status</h5><button class="hms-icon-btn" @click="updateModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Status</label>
              <select v-model="upd.current_status" class="hms-input">
                <option value="active">Active</option><option value="transferred_out">Transferred out</option>
                <option value="self_transfer">Self-transfer</option><option value="lost">Lost to follow-up</option>
                <option value="died">Died</option><option value="stopped">Stopped</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Status change date</label><input v-model="upd.status_change_date" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Pregnancy</label>
              <select v-model="upd.pregnancy_status" class="hms-input">
                <option value="not_applicable">N/A</option><option value="not_pregnant">Not pregnant</option>
                <option value="pregnant">Pregnant</option><option value="breastfeeding">Breastfeeding</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">TB status</label>
              <select v-model="upd.tb_status" class="hms-input">
                <option value="not_screened">Not screened</option><option value="no_signs">No signs</option>
                <option value="presumptive">Presumptive</option><option value="on_treatment">On treatment</option><option value="completed">Completed</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Next appointment</label><input v-model="upd.next_appointment_date" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Disclosure</label>
              <select v-model="upd.disclosure" class="hms-input">
                <option value="full">Full</option><option value="partial">Partial</option><option value="none">None</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="upd.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="updateModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveUpdate">{{ saving?'Saving…':'Update' }}</button>
        </div>
      </div>
    </div>

    </div> <!-- /canAccess v-else -->

  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "ArtRegister",
  components: { Navigation },
  data() {
    return {
      tab: 'clients', saving: false,
      canAccess: false,
      stats: {}, defaulterStats: {},
      regimens: [],

      clientSearch: '',
      clients: [],

      detail: { client: null, visits: [], viralLoads: [], regimenChanges: [] },

      defaulters: [],
      vlWatch: [],

      indicatorPeriod: 'this_month',
      indicators: {},
      suppressionPct: 0,

      enrolModal: false, enrol: this.blankEnrol(),
      visitModal: false, visit: this.blankVisit(),
      vlModal: false,    vl:    this.blankVl(),
      switchModal: false, switchForm: { to_regimen_id: null, to_line: '1st', reason: 'side_effects', changed_at: '', notes: '' },
      updateModal: false, upd: this.blankUpdate()
    };
  },
  async mounted() {
    try {
      var r = await executeJson({ function: 'canAccessSensitiveRegister', module: 'art' }, hms);
      this.canAccess = !!(r.data && r.data.success && r.data.data && r.data.data.can);
    } catch (e) { this.canAccess = false; }
    if (this.canAccess) this.loadAll();
  },
  methods: {
    blankEnrol() {
      return {
        patient_id: null, patient_name: '', patient_search: '',
        ccc_number: '', enrollment_date: new Date().toISOString().substring(0,10),
        entry_point: 'OPD', who_stage: null,
        baseline_cd4: null, baseline_cd4_date: '', baseline_vl: null, baseline_vl_date: '',
        date_started_art: new Date().toISOString().substring(0,10),
        current_regimen_id: null, regimen_line: '1st',
        pregnancy_status: 'not_applicable', tb_status: 'not_screened',
        next_appointment_date: '',
        treatment_supporter_name: '', treatment_supporter_phone: '',
        chv_name: '', chv_phone: '', notes: ''
      };
    },
    blankVisit() {
      return {
        visit_date: new Date().toISOString().substring(0,10), visit_type: 'refill',
        weight_kg: null, height_cm: null, bp_systolic: null, bp_diastolic: null,
        who_stage: null, functional_status: 'working', adherence: 'good', adherence_pct: null,
        side_effects: '', opportunistic_infections: '',
        pregnancy_status: 'not_applicable', fp_method: '', tb_screening: 'no_signs',
        cotrim_dispensed: 0, inh_dispensed: 0,
        arvs_dispensed: '', days_dispensed: 30, next_appointment: '', notes: ''
      };
    },
    blankVl() { return { collection_date: new Date().toISOString().substring(0,10), result_date: '', vl_copies: null, is_undetectable: 0, sample_type: 'plasma', vl_indication: 'annual', lab_name: '', notes: '' }; },
    blankUpdate() { return { current_status: 'active', status_change_date: '', pregnancy_status: 'not_applicable', tb_status: 'not_screened', next_appointment_date: '', disclosure: 'none', notes: '' }; },

    async loadAll() {
      await Promise.all([this.loadRegimens(), this.loadClients(), this.loadDefaulters()]);
    },
    async loadRegimens() {
      try {
        const r = await executeJson({ function: 'getArtRegimens' }, hms);
        if (r.data && r.data.success) this.regimens = (r.data.data && r.data.data.Regimens) || [];
      } catch (e) { console.error(e); }
    },
    async loadClients() {
      try {
        const r = await executeJson({ function: 'getArtClients', search: this.clientSearch }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.clients = d.Clients || [];
          this.stats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },

    // ENROL
    openEnrolModal() { this.enrol = this.blankEnrol(); this.enrolModal = true; },
    async findEnrolPatient() {
      const q = (this.enrol.patient_search || '').trim();
      if (!q) return;
      const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
      if (r.data && r.data.success) {
        const d = r.data.data || {};
        const p = (d.Patients || d.patients || [])[0];
        if (!p) { alert('No patient found.'); return; }
        this.enrol.patient_id = p.patient_id;
        this.enrol.patient_name = p.first_name + ' ' + p.last_name;
      }
    },
    async saveEnrol() {
      this.saving = true;
      try {
        const params = { function: 'enrolArtClient', ...this.enrol };
        delete params.patient_search; delete params.patient_name;
        const r = await executeJson(params, hms);
        if (r.data && r.data.success) { this.enrolModal = false; await this.loadClients(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    // DETAIL
    async openDetail(c) { this.tab = 'detail'; await this.openDetailById(c.art_id); },
    async openDetailById(artId) {
      try {
        const r = await executeJson({ function: 'getArtClientDetail', art_id: artId }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.detail.client = d.Client;
          this.detail.visits = d.Visits || [];
          this.detail.viralLoads = d.ViralLoads || [];
          this.detail.regimenChanges = d.RegimenChanges || [];
          this.tab = 'detail';
        }
      } catch (e) { console.error(e); }
    },

    // VISIT
    openVisitModal() { this.visit = this.blankVisit(); this.visitModal = true; },
    async saveVisit() {
      if (!this.detail.client) return;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'recordArtVisit', art_id: this.detail.client.art_id, ...this.visit }, hms);
        if (r.data && r.data.success) { this.visitModal = false; await this.openDetailById(this.detail.client.art_id); await this.loadClients(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    // VIRAL LOAD
    openVlModal() { this.vl = this.blankVl(); this.vlModal = true; },
    async saveVl() {
      if (!this.detail.client) return;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'recordViralLoad', art_id: this.detail.client.art_id, ...this.vl }, hms);
        if (r.data && r.data.success) { this.vlModal = false; await this.openDetailById(this.detail.client.art_id); await this.loadClients(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    // SWITCH
    openSwitchModal() {
      this.switchForm = { to_regimen_id: null, to_line: this.detail.client.regimen_line || '1st', reason: 'side_effects', changed_at: new Date().toISOString().substring(0,10), notes: '' };
      this.switchModal = true;
    },
    async saveSwitch() {
      if (!this.detail.client) return;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'changeArtRegimen', art_id: this.detail.client.art_id, ...this.switchForm }, hms);
        if (r.data && r.data.success) { this.switchModal = false; await this.openDetailById(this.detail.client.art_id); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    // UPDATE
    openUpdateModal() {
      const c = this.detail.client;
      this.upd = {
        current_status: c.current_status,
        status_change_date: c.status_change_date || '',
        pregnancy_status: c.pregnancy_status,
        tb_status: c.tb_status,
        next_appointment_date: c.next_appointment_date || '',
        disclosure: c.disclosure || 'none',
        notes: ''
      };
      this.updateModal = true;
    },
    async saveUpdate() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'updateArtClient', art_id: this.detail.client.art_id, ...this.upd }, hms);
        if (r.data && r.data.success) { this.updateModal = false; await this.openDetailById(this.detail.client.art_id); await this.loadClients(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async loadDefaulters() {
      try {
        const r = await executeJson({ function: 'getArtDefaulters' }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.defaulters = d.Defaulters || [];
          this.defaulterStats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },
    async loadVlWatch() {
      try {
        const r = await executeJson({ function: 'getArtVlWatchlist' }, hms);
        if (r.data && r.data.success) this.vlWatch = (r.data.data && r.data.data.Watchlist) || [];
      } catch (e) { console.error(e); }
    },
    async loadIndicators() {
      try {
        const r = await executeJson({ function: 'getArtIndicators', period: this.indicatorPeriod }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.indicators = d.Indicators || {};
          this.suppressionPct = d.SuppressionPct || 0;
        }
      } catch (e) { console.error(e); }
    },

    // helpers
    rowClass(c) {
      if (c.days_to_appointment != null && c.days_to_appointment < -7) return 'row-warn';
      if (c.latest_vl != null && c.latest_vl_suppressed == 0) return 'row-warn';
      return '';
    },
    statusPill(s) {
      return ({ active: 'hms-pill-success', transferred_out: 'hms-pill-info', self_transfer: 'hms-pill-info',
                lost: 'hms-pill-danger', died: 'hms-pill-muted', stopped: 'hms-pill-warn' })[s] || '';
    },
    adherencePill(a) { return ({ good: 'hms-pill-success', fair: 'hms-pill-warn', poor: 'hms-pill-danger' })[a] || ''; },
    formatIndication(i) {
      return ({ routine_6m: 'Routine 6m', routine_12m: 'Routine 12m', annual: 'Annual',
                high_vl_repeat: 'Repeat (high VL)', clinical_failure: 'Clinical failure',
                suspected_failure: 'Suspected failure', baseline: 'Baseline', pregnancy: 'Pregnancy' })[i] || i;
    },
    formatChangeReason(r) {
      return ({ treatment_failure: 'Treatment failure', side_effects: 'Side effects',
                tb_treatment: 'TB treatment', pregnancy: 'Pregnancy', stockout: 'Stock-out',
                simplification: 'Simplification', new_guideline: 'New guideline', other: 'Other' })[r] || r;
    },
    ageString(dob) {
      if (!dob) return '';
      const d = new Date(dob.replace ? dob.replace(' ','T') : dob);
      const days = Math.floor((Date.now() - d.getTime()) / 86400000);
      if (days < 365 * 2) return Math.floor(days / 30) + 'mo';
      return Math.floor(days / 365) + 'y';
    }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 28px; font-weight: 700; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-success { color: #276749; } .hms-stat-info { color: #2c5282; }
.hms-stat-warning { color: #c05621; } .hms-stat-danger { color: #c53030; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }
.hms-tab:disabled { opacity: .4; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }

.hms-grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }

.hms-name-big { font-size: 20px; font-weight: 700; color: #1a202c; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-empty { background: #fff; border: 1px dashed #cbd5e0; border-radius: 8px; padding: 48px; text-align: center; color: #718096; }
.hms-empty .mdi { font-size: 48px; color: #38a169; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }
.hms-info-banner { background: #ebf8ff; border: 1px solid #90cdf4; color: #2c5282; padding: 8px 10px; border-radius: 6px; margin-bottom: 12px; font-size: 13px; }
.hms-patient-chip { display: inline-flex; align-items: center; gap: 4px; background: #ebf8ff; color: #2c5282; padding: 6px 10px; border-radius: 6px; margin-top: 6px; font-size: 13px; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-amber     { background: #d69e2e; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-link      { background: transparent; color: #2b6cb0; border: none; cursor: pointer; }

.hms-ind-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #edf2f7; font-size: 14px; }
.hms-ind-row strong { color: #2d3748; font-size: 16px; }

.hms-suppression { margin-top: 14px; }
.hms-supp-label  { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; }
.hms-supp-bar    { background: #edf2f7; border-radius: 6px; height: 18px; overflow: hidden; margin: 4px 0; }
.hms-supp-fill   { background: linear-gradient(90deg, #38a169, #276749); height: 100%; }
.hms-supp-num    { text-align: right; font-weight: 700; color: #276749; font-size: 18px; }

.st-good { color: #276749; font-weight: 600; }
.st-bad  { color: #c53030; font-weight: 600; }
.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }
</style>
